import React, { useEffect, useRef, useState } from 'react';
import { Style } from './types';
import { FieldError, createField } from './field';
import BaseInput from '../BaseInput';

const DEFAULT_BORDER = '1px solid #CBD5E1';
const MISSING_BORDER = '2px solid #FF0000';

export default function VgsCollectValue({
  name,
  placeholder,
  label,
  validations,
  maskInput,
  type,
  required,
  showRequiredAsterisk,
  serializers,
  style,
  form,
  onDirtyChange,
  onEmptyChange,
  onInvalidChange,
  inputClass,
  fieldErrors,
}: {
  name: string;
  label: string;
  validations?: RegExp[];
  maskInput?: MaskInput;
  placeholder?: string;
  type: VgsFieldType;
  style?: Style;
  required: boolean;
  showRequiredAsterisk: boolean;
  serializers?: VgsSerializer[];
  onDirtyChange?: (fieldName: string, isDirty: boolean) => void;
  onEmptyChange: (fieldName: string, isEmpty: boolean) => void;
  onInvalidChange: (fieldName: string, isValid: boolean) => void;
  form: VGSForm<unknown>;
  inputClass?: string;
  fieldErrors?: FieldError[];
}): JSX.Element {
  const elRef = useRef<HTMLDivElement>(null);
  const [field, setField] = useState<VGSField>();
  const [isDirty, setIsDirty] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const errorMessage = fieldErrors?.find(
    (fe) => fe.fieldName === name,
  )?.message;

  useEffect(() => {
    if (
      field !== undefined ||
      elRef.current === undefined ||
      form.fields.some((f) => f.name === name)
    ) {
      return;
    }

    setField(
      createField(
        type,
        serializers || [],
        form,
        elRef,
        name,
        placeholder,
        validations,
        style,
        maskInput,
      ),
    );
  }, [
    field,
    form,
    isDirty,
    maskInput,
    name,
    placeholder,
    serializers,
    style,
    type,
    validations,
  ]);

  useEffect(() => {
    if (field === undefined) {
      return;
    }

    const onUpdate = (fieldState: VGSFieldState): void => {
      if (onDirtyChange !== undefined) {
        onDirtyChange(name, fieldState.isDirty);
      }
      if (required) {
        onEmptyChange(name, fieldState.isEmpty);
      }
      onInvalidChange(name, !fieldState.isValid);

      setIsEmpty(fieldState.isEmpty);
      setIsDirty(fieldState.isDirty);
      setIsValid(fieldState.isValid);
    };

    field.on('update', onUpdate);
  }, [
    field,
    form,
    name,
    onDirtyChange,
    onEmptyChange,
    onInvalidChange,
    required,
  ]);

  useEffect(() => {
    return () => field?.delete();
  }, [field]);

  return (
    <BaseInput
      label={label}
      border={isEmpty && isDirty && required ? MISSING_BORDER : DEFAULT_BORDER}
      required={required}
      showRequiredAsterisk={showRequiredAsterisk}
      invalid={!isValid}
      dirty={isDirty}
      empty={isEmpty}
      name={name}
      className={inputClass}
      errorMessage={errorMessage}
    >
      <div className='vgs-ref' ref={elRef}></div>
    </BaseInput>
  );
}
